import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { RIDE_STATUSES, CLASS_RESULTS_STATUSES, showSectionStyle } from "score-common";
import { useAnnouncerContext } from "./AnnouncerContext";
import { ringsFor } from "../utils/rings";
import { useClassesForShow } from "../hooks/useClassesForShow";
import { useRidesForRing } from "../hooks/useRidesForRing";
import { useScoreApi } from "../api/scoreApi";
import { useBreaksForRing } from "../hooks/useBreaksForRing";
import { usePeople, useHorses, useTestsForRides, useCompositeJudgeNames } from "../hooks/useRoster";
import { useRideStatuses } from "../hooks/useRideStatuses";
import { useRideSheets } from "../hooks/useRideSheets";
import { useRingStateForRing } from "../hooks/useRingStateForRing";
import { useClassResultsSummary } from "../hooks/useClassResultsSummary";
import { useObservedPresence } from "../hooks/useObservedPresence";
import { presenceRowsFor } from "../utils/presence";
import { useRingSettings } from "../hooks/useRingSettings";
import { useNow } from "../hooks/useNow";
import { useShowChangeCheck } from "../hooks/useShowChangeCheck";
import { useStaffChangeNudge } from "../hooks/useStaffChangeNudge";
import { STALE_THRESHOLD_MS, CLOCK_TICK_MS, LIVE_TICK_MS } from "../constants/polling";
import {
  buildOrderOfGoRows,
  ROW_KIND,
  RIDE_ROLE,
  resolveRingTruth,
  resolveLiveClassId,
  findRoleRideId,
  groupRowsByClassId,
  preserveRevealedResults,
} from "../utils/orderOfGo";
import { venueDayKey, venueTodayKey } from "../utils/formatTime";

// #2137 (docs/score/announcer-ui-redesign.md §11) — the ring poll set,
// lifted out of OrderOfGo.jsx unchanged in behaviour. The redesigned shell
// has four bands (NavRail, day spine, live desk, look-up column) that all
// need the SAME ring data; none of them may open a second copy of these
// polls — a venue tablet on show wifi cannot afford it. This provider is the
// ONE place that calls useClassesForShow/useRidesForRing/useBreaksForRing/
// usePeople/useHorses/useTestsForRides/useRideStatuses/useRideSheets/
// useRingStateForRing/useClassResultsSummary/useObservedPresence for a given
// mount — every consumer reads the result via useRingData() instead of
// calling any of those hooks itself.
//
// Every comment on the moved logic below is carried over verbatim (or
// lightly adapted for "provider" vs. "component") from OrderOfGo.jsx (card
// #642 and its many follow-ups) — the reasoning behind each piece didn't
// change, only where it lives.
const RingDataContext = createContext(null);

export function RingDataProvider({ children }) {
  const { selectedShow, selectedRing, ringResolved, showLoading, online, browserOnline, classFetchOwner } =
    useAnnouncerContext();
  const showId = selectedShow?.id;

  // #926/#1857 — per-ring settings map (auto-scroll, all-days), keyed by ring
  // id; see src/hooks/useRingSettings.js for the storage shape and the
  // unresolved-`selectedRing` write guard.
  const { currentRingSettings, updateRingSetting } = useRingSettings(selectedRing);

  // #3557 (docs/adr/0003) -- nothing below polls on its own clock any more.
  // One change check (GET /show/:id/changes) decides what to re-read:
  //   classTick -> the class list, ringTick -> this ring's rides / breaks /
  //   ring-state / results summary / statuses, showTick -> the judge roster.
  // A class counter only ever moves together with its ring's counter, so the
  // class list rides the ring tick (plus a change in the set of ring names).
  // Bumping a tick is what makes the hook revalidate: it re-runs on its deps.
  // The tick is the refetch nonce; the 60 s backstop bumps all of them.
  // #3562: the staff WebSocket nudge for this show and ring (inert without a
  // VITE_SCORE_WS_URL, a token, or while Instant updates is OFF for the show).
  useStaffChangeNudge(showId, selectedRing, { enabled: online });
  const { ringTick, classTick, showTick, wholeShowTick, lastCheckedAt } = useShowChangeCheck(
    showId,
    selectedRing,
    { enabled: online },
  );

  // #892 — all-rides vs today-only toggle. Per-ring (#926): default OFF
  // (today-only) for a never-seen ring — the announcer normally works one day
  // at a time, matching scribe-fe's #910 "today by default" intent.
  const showAllDays = currentRingSettings.showAllDays;
  const setShowAllDays = useCallback(
    (enabled) => updateRingSetting("showAllDays", enabled),
    [updateRingSetting],
  );
  // #691 — the "Auto Scroll to Current Ride" switch's persisted state. The
  // actual scrollIntoView call stays owned by OrderOfGo.jsx's render layer
  // (#909 — the ONE place that calls it); this context only carries the
  // per-ring preference so the switch survives a reload / ring switch.
  const autoScrollEnabled = currentRingSettings.autoScrollEnabled;
  const setAutoScrollEnabled = useCallback(
    (enabled) => updateRingSetting("autoScrollEnabled", enabled),
    [updateRingSetting],
  );
  // "Today" as the VENUE sees it (#688), never the browser's own zone. #1813 —
  // venueTodayKey takes a genuine instant, so (unlike venueDayKey(scheduledTime)
  // below, which stays on the fixed FVD stamp zone) it needs the show's real
  // venueTimezone to land on the same calendar day; see formatTime.js's
  // venueTodayKey comment for the full reasoning. Cheap to recompute — this
  // provider already re-renders on every poll/clock tick.
  const todayKey = venueTodayKey(undefined, selectedShow?.venueTimezone);

  // #692 — classes revalidate on a change tick (previously they never accepted
  // a nonce at all, so a renamed/renumbered or newly-added class could sit
  // stale in the header forever after setup).
  const {
    data: classes,
    loading: classesLoading,
    refetch: refetchClasses,
    refreshing: classesRefreshing,
    error: classesError,
  } = useClassesForShow(showId, classTick, { keyed: true, shareWith: classFetchOwner });

  // #926 — every ring for THIS show, from the already-fetched class list
  // (same source AnnouncerContext's default-ring resolution uses —
  // score-api has no ring-listing route). Feeds the ring switcher.
  const rings = useMemo(() => ringsFor(classes), [classes]);

  const {
    data: rides,
    loading: ridesLoading,
    refetch: refetchRides,
    refreshing: ridesRefreshing,
    error: ridesError,
  } = useRidesForRing(showId, selectedRing, ringTick);
  const {
    data: breaks,
    loading: breaksLoading,
    refetch: refetchBreaks,
    refreshing: breaksRefreshing,
    error: breaksError,
  } = useBreaksForRing(showId, selectedRing, ringTick);
  // #2144/#2233 — useRemoteResource only ever sets `error` when NOTHING has
  // loaded yet for the current [showId, ring] key (a cold start or a ring
  // switch's first fetch failing; see that hook's own doc), and clears it
  // again the moment a fetch actually lands. That is exactly the window
  // where `rides`/`breaks` sit at their `null` "nothing yet" value and
  // `filteredRides`/`filteredBreaks` below collapse to `[]` — the SAME shape
  // a genuinely empty ring produces. Without this flag the status bar and
  // OrderOfGo's empty state have no way to tell "the ring has no rides" from
  // "we could not load them," and #2233 is exactly an announcer being told
  // the confident falsehood "No rides scheduled for this ring" off a failed
  // fetch.
  //
  // #3164 -- the classes fetch has the identical false-empty failure: a
  // failed GET /class leaves `classes` null (same shape as a show with no
  // classes) with loading false, so DaySpine printed "No classes scheduled"
  // off a failed fetch. Folded INTO hasFetchError (not a separate flag)
  // because every hasFetchError consumer wants the same answer for it:
  // DaySpine swaps in its could-not-load copy, StatusBar shows its generic
  // "could not load" wording and warning severity, and LiveDesk refuses to
  // believe END_OF_DAY. Those are all generic "the ring's data did not load"
  // surfaces. hasRidesFetchError below stays rides-only on purpose.
  const hasFetchError = Boolean(ridesError) || Boolean(breaksError) || Boolean(classesError);
  // #2144 round 2 (judge MUST-FIX 2) — the combined OR above is right for
  // StatusBar's generic "could not load" wording, but oog.emptyFetchError
  // names RIDES specifically ("Could not load rides for this ring"). A
  // breaks-only cold-start failure on an otherwise-empty ring must not print
  // that sentence when the rides fetch actually succeeded, so the empty
  // state gets its own, narrower signal.
  const hasRidesFetchError = Boolean(ridesError);
  const { data: people } = usePeople();
  // #3419 -- a composite booth lists every real judge, not the FVD run-together name.
  const { data: compositeNames } = useCompositeJudgeNames(showId, showTick);
  const { data: horses } = useHorses();

  // #892 — today-only view filters the RAW entities BEFORE buildOrderOfGoRows
  // ever sees them (rather than filtering the built rows afterwards), so
  // CURRENT/ON_DECK/NEXT role assignment — and the test-fetch/status-poll ID
  // lists right below — stay scoped to today's rides, exactly like the
  // announcer's existing single-day behavior always has. A ride/break with no
  // derivable day (no scheduledTime at all) is kept regardless of the toggle:
  // it can't be dated, so it shouldn't silently vanish because of that.
  const filteredRides = useMemo(() => {
    if (showAllDays) return rides || [];
    return (rides || []).filter((r) => {
      const day = venueDayKey(r.scheduledTime, r.scheduledTimeZone);
      return !day || day === todayKey;
    });
  }, [rides, showAllDays, todayKey]);
  const filteredBreaks = useMemo(() => {
    if (showAllDays) return breaks || [];
    return (breaks || []).filter((b) => {
      const day = venueDayKey(b.scheduledTime, b.scheduledTimeZone);
      return !day || day === todayKey;
    });
  }, [breaks, showAllDays, todayKey]);

  const testIds = useMemo(
    () => (filteredRides || []).map((r) => r.testId).filter(Boolean),
    [filteredRides],
  );
  const { data: tests } = useTestsForRides(testIds);

  // Poll the SINGLE server-derived status for every ride in the ring (#544/#546).
  // Only meaningful while online — a hidden tab is already handled inside the hook.
  const rideIds = useMemo(() => (filteredRides || []).map((r) => r.id), [filteredRides]);
  const {
    statuses,
    lastUpdated: statusesLastUpdated,
    loading: statusesRefreshing,
    refresh: refreshStatuses,
  } = useRideStatuses(rideIds, {
    // #3557 -- no interval: statuses re-read when the ring's counter moves
    // (the effect below), and once whenever the ride-id set changes.
    enabled: online && rideIds.length > 0,
  });
  // Skip tick 0 (the mount fetch is the hook's own) and the backstop is already
  // folded into ringTick.
  const lastStatusTickRef = useRef(0);
  useEffect(() => {
    if (ringTick === lastStatusTickRef.current) return;
    lastStatusTickRef.current = ringTick;
    refreshStatuses();
  }, [ringTick, refreshStatuses]);
  // #2070 — ScoreSheets for the same ride-id set, so deriveRideTermReason
  // can resolve a sheet-level elimination that hasn't been denormalized
  // onto ride.terminationReason yet (see useRideSheets.js).
  const { sheetsByRideId, refresh: refreshSheets } = useRideSheets(rideIds, {
    enabled: online && rideIds.length > 0,
  });

  // #2131 (docs/score/ring-truth.md) — the ring's OWN stored ring-state row,
  // the ONE input CURRENT/ON_DECK role assignment reads (assignRoles in
  // orderOfGo.js). Same entityTick revalidate beat as classes/rides/breaks
  // (#692) rather than a separate poll cadence for one small row.
  const {
    data: ringState,
    loading: ringStateLoading,
    refetch: refetchRingState,
  } = useRingStateForRing(showId, selectedRing, ringTick);
  // Rule 8 (docs/score/ring-truth.md) — resolved once here so every consumer
  // (the sticky "no class in progress" banner and buildOrderOfGoRows's role
  // assignment) reads the identical answer, never two separate opinions.
  const ringTruth = useMemo(() => resolveRingTruth(ringState), [ringState]);
  // #2197 — true only once ring-state has answered for a REAL ring, not
  // useRingStateForRing's synthetic "ring not resolved yet" settle
  // (undefined, see that hook's comment). `ringStateLoading` alone can't
  // tell these apart: useRemoteResource's `loading` only ever covers the
  // FIRST fetch this mount has ever settled, and the ring-not-known case
  // settles first (near-instantly, before the ring even resolves), so
  // `ringStateLoading` reads false throughout the real, ring-resolved fetch
  // too. `ringState !== undefined` is what actually distinguishes "no
  // answer yet" from "server confirmed no live class" (a real null).
  const ringStateKnown = !ringStateLoading && ringState !== undefined;
  // #2087 — the batch status poll's OWN "we don't know yet" signal, parallel
  // to #2086's `resultsUnknown` in ClassResults. `statusesLastUpdated` is set
  // only after a fetch actually lands (packages/common-fe/src/utils/useRideStatuses.js);
  // it never resets once set (a later failure keeps the last-known statuses
  // per #705's degraded mode, "known, stale" rather than "unknown"), so this
  // is true exactly while the poll has never once answered OR is failing
  // persistently before its first success — precisely the window where
  // normalizeStatus's PENDING default (orderOfGo.js) is a guess, not a fact.
  const statusesUnknown = statusesLastUpdated == null;

  // #908 — manual "refresh now". Fires all four data sources that make up
  // the Order of Go: classes/rides/breaks each go through
  // useRemoteResource's refetch() (forces a fresh fetch immediately), and
  // refreshStatuses()/refreshSheets()/refetchRingState() do the same for
  // their own pollers. Deliberately does NOT touch scroll position —
  // scrollToCurrent (#909) stays owned by the render layer.
  const handleRefresh = useCallback(() => {
    refetchClasses();
    refetchRides();
    refetchBreaks();
    refreshStatuses();
    refreshSheets();
    refetchRingState();
  }, [
    refetchClasses,
    refetchRides,
    refetchBreaks,
    refreshStatuses,
    refreshSheets,
    refetchRingState,
  ]);
  // #3596 -- Dismiss an active alert. Any staff screen can do it and it ends
  // the alert everywhere (server removes it from Ride.utilityEvents; a second
  // dismiss of an already-dismissed alert is a success there too). Re-reads the
  // ring's rides right away so the alert leaves this screen without waiting for
  // the next change tick. A failure rethrows so the Dismiss button can say so.
  const scoreApi = useScoreApi();
  const dismissAlert = useCallback(
    async (rideId, eventId) => {
      await scoreApi.rides.deleteRideUtilityEvent(rideId, eventId);
      refetchRides();
    },
    [scoreApi, refetchRides],
  );
  const refreshing =
    classesRefreshing || ridesRefreshing || breaksRefreshing || statusesRefreshing;

  // #645 — freshness banner. #3557: statuses no longer poll on a beat, so a
  // quiet ring would age the banner into "stale" with nothing wrong. A
  // SUCCESSFUL change check ("nothing moved, still live") counts as fresh too:
  // the banner shows the later of the last status read and the last good
  // check. A failing check stops advancing it, and the screen goes stale.
  // Only once statuses have landed at least once: a good check alone must not
  // call a screen "live" before its data has loaded.
  const lastUpdated = useMemo(() => {
    if (!statusesLastUpdated) return null;
    if (lastCheckedAt && lastCheckedAt.getTime() > statusesLastUpdated.getTime()) return lastCheckedAt;
    return statusesLastUpdated;
  }, [statusesLastUpdated, lastCheckedAt]);
  const now = useNow(CLOCK_TICK_MS);
  const isStale = Boolean(lastUpdated) && now - lastUpdated.getTime() > STALE_THRESHOLD_MS;
  // #691 — sticky header's live clock + countdown, and presence's
  // elapsed-time math below, share this one tick; pure re-render trigger, no
  // network.
  const liveNow = useNow(LIVE_TICK_MS);

  // #3296 -- the show's own section name / numbering (Show.sectionLabel /
  // sectionNumbering, #3295), so a row carries the rider's section ("Heat B").
  const sectionLabelSetting = selectedShow?.sectionLabel;
  const sectionNumberingSetting = selectedShow?.sectionNumbering;
  const sectionStyle = useMemo(
    () => showSectionStyle({ sectionLabel: sectionLabelSetting, sectionNumbering: sectionNumberingSetting }),
    [sectionLabelSetting, sectionNumberingSetting],
  );

  const rows = useMemo(
    () =>
      buildOrderOfGoRows({
        rides: filteredRides,
        breaks: filteredBreaks,
        classes: classes || [],
        people: people || [],
        horses: horses || [],
        tests: tests || [],
        statuses: statuses || {},
        sheetsByRideId,
        ringState,
        sectionStyle,
        compositeNames,
      }),
    [filteredRides, filteredBreaks, classes, people, horses, tests, statuses, sheetsByRideId, ringState, sectionStyle, compositeNames],
  );

  // #644/#2131 — results/placings. The "live" class is the ring-state row's
  // OWN classId whenever it's in progress: that's the one whose results can
  // still change from one status poll to the next, so it's the only one
  // that needs a slow results poll of its own (docs/score/announcer.md
  // freshness table). Every other class fetches once.
  //
  // Deliberately NOT "whichever row has role CURRENT" (judge finding (d)):
  // ringTruth.rideId is nullable (rule 5 / AC17 — Start class names a class
  // before any of its rides has been opened), so a genuinely in-progress
  // class can have zero rows tagged CURRENT. resolveLiveClassId reads
  // ringTruth directly and has no such gap.
  const liveClassId = resolveLiveClassId(ringTruth);

  // #2147 — the day spine's `placed` marker for every class in the ring, in
  // ONE call (docs/score/announcer-ui-redesign.md §10 Gap 1), rides the same
  // shared entityTick as classes/rides/breaks above. See ClassResults.jsx's
  // `shouldPollDetail` for the poll gate this `placed` value does NOT drive.
  const { summaryByClassId, loading: summaryLoading } = useClassResultsSummary(showId, selectedRing, ringTick);
  const isClassPlacedFromSummary = useCallback(
    (classId) => summaryByClassId[classId]?.status === CLASS_RESULTS_STATUSES.PLACED,
    [summaryByClassId],
  );

  // #2366 — scribe presence for the CURRENT ride's class only ("while a
  // ride is up", AC1). Polls GET /presence at the same cadence scribe-fe
  // heartbeats (HEARTBEAT_INTERVAL_MS); a failed poll is silently additive
  // (see useObservedPresence's own header) so this can never degrade the
  // running order. `liveNow` drives the elapsed-time/quiet-threshold math on
  // every render, independent of the poll cadence.
  const observedPresence = useObservedPresence(showId, selectedRing, liveClassId);
  const presenceRows = useMemo(
    () => presenceRowsFor(observedPresence, liveNow),
    [observedPresence, liveNow],
  );

  // #2626 — join source for the look-up column's results/ride list. Built
  // from ALL of the ring's rides/breaks (unfiltered by todayKey/showAllDays),
  // NOT from `rows` above: `rows` is day-scoped for the live desk, but the
  // day spine's shownDayKey lets the announcer click a class on a day the
  // day-scoped `rows` never included (see this context's `rides`/`breaks`
  // comment further down). Using `rows` here left rideRowsByClassId empty
  // for that class, which is what rendered nameless em-dash results and an
  // empty ride list underneath — the ride ROWS to join against were simply
  // missing, not the results fetch (card #2626, candidate cause 1 of 3
  // confirmed by reproduction).
  //
  // For the live class specifically this is unchanged: showAllDays off
  // still means today's rides are the only ones a currently-running class
  // could have, so `allRows`'s classId grouping matches `rows`'s exactly
  // for the live class (AC6 — no regression there).
  const allRows = useMemo(
    () =>
      buildOrderOfGoRows({
        rides: rides || [],
        breaks: breaks || [],
        classes: classes || [],
        people: people || [],
        horses: horses || [],
        tests: tests || [],
        statuses: statuses || {},
        sheetsByRideId,
        ringState,
        sectionStyle,
        compositeNames,
      }),
    [rides, breaks, classes, people, horses, tests, statuses, sheetsByRideId, ringState, sectionStyle, compositeNames],
  );
  const rideRowsByClassId = useMemo(() => groupRowsByClassId(allRows), [allRows]);

  // classId -> raw GET /class/:classId/results array, lifted up from each
  // ClassResults instance so RideRow can badge its own placing too (the
  // class-level list and the per-ride badge are fed by the SAME fetch/poll,
  // never a second independent one).
  const [resultsByClassId, setResultsByClassId] = useState({});
  const handleClassResults = useCallback((classId, classResults) => {
    setResultsByClassId((prev) => {
      // #2445/#1647: latch here too (not only inside each useClassResults),
      // because a freshly mounted copy (a background copy of a class the
      // announcer was followed away from) starts with an empty ref, so its
      // first, possibly regressed, not-final read must not un-reveal a
      // result an earlier copy already revealed.
      const next = preserveRevealedResults(prev[classId], classResults);
      if (prev[classId] === next) return prev;
      return { ...prev, [classId]: next };
    });
  }, []);

  // #909's `currentRef`/scrollIntoView stays in OrderOfGo.jsx (a DOM concern
  // tied to that component's own list), but WHICH ride is CURRENT is shared
  // ring data, so it's derived here off the same `rows` every consumer reads.
  const currentRideId = findRoleRideId(rows, RIDE_ROLE.CURRENT);

  // #919 — a LIVE-SHOW regression. Confirming a ride's marks (all judges
  // confirmed, #697/#698) flips that ride's OWN status off IN_PROGRESS.
  // #2131 changed WHERE CURRENT comes from (the ring's stored ring-state
  // row, via assignRoles() in orderOfGo.js, not a per-render hunt for an
  // IN_PROGRESS ride) but not the heuristic below: a scribe moving on to a
  // different ride in the ring still reassigns CURRENT, and auto-scroll must
  // not yank the screen away from the ride the announcer was mid-read on,
  // right as its judge marks were revealed — exactly when the operator needs
  // to control position, not have it taken from them.
  //
  // Distinguish WHY current changed by looking at the OUTGOING current
  // ride's own status this render, not the incoming one:
  //   - it left IN_PROGRESS (confirmed / completed / final / scratched) ->
  //     CURRENT moved because the ride the operator was looking at is DONE,
  //     not because the ring genuinely moved on. Don't scroll — #919.
  //   - it's still IN_PROGRESS -> a later ride has genuinely overtaken it
  //     (#921's multi-IN_PROGRESS case, e.g. the next horse already entered
  //     before the previous one's marks were confirmed) -> that's real ring
  //     progression, follow it.
  //   - no previous current at all -> first paint, scroll straight to it.
  // `scrollRequest` is bumped (never the scroll call itself, which stays in
  // OrderOfGo.jsx per #909) exactly on the "scroll straight to it" and "ring
  // genuinely progressed" cases below; OrderOfGo's own effect on
  // `scrollRequest` is what actually calls scrollToCurrent(), gated on
  // autoScrollEnabled there.
  //
  // statusByRideId is kept in a REF, not an effect dependency: `rows` (and
  // therefore a status map derived from it) gets a new reference on EVERY
  // poll tick, including ones where currentRideId itself doesn't change.
  // Depending on it directly would re-run the decision below — and
  // therefore potentially re-request a scroll — on every tick, not only on
  // an actual CURRENT change. The effect must stay keyed on currentRideId
  // alone, exactly like the pre-#919 version, and just read the freshest
  // status snapshot when it does fire.
  const statusByRideIdRef = useRef(new Map());
  useEffect(() => {
    const map = new Map();
    for (const row of rows) {
      if (row.kind === ROW_KIND.RIDE) map.set(row.id, row.status);
    }
    statusByRideIdRef.current = map;
  }, [rows]);

  // #2131 — currentRideId (above) comes from the ring's stored ring-state
  // row, polled on `entityTick`; row.status (statusByRideIdRef, above) comes
  // from the SEPARATE useRideStatuses poll, on its own STATUS_POLL_MS timer.
  // Those two timers are independent, so the render where currentRideId
  // first reflects a reassignment can predate the render where the outgoing
  // ride's OWN status has caught up — deciding outgoingStillInProgress off
  // whatever statusByRideIdRef happens to hold AT THAT INSTANT reads stale
  // data and wrongly auto-scrolls on a plain confirm (#919's bug,
  // reintroduced by the poll race). Force a fresh status fetch the moment a
  // reassignment is seen, and DEFER the decision to the second effect below,
  // which fires once that fresh snapshot actually lands.
  const pendingReassignmentRef = useRef(null);
  const prevCurrentRideIdRef = useRef(null);
  const [scrollRequest, setScrollRequest] = useState(0);
  useEffect(() => {
    if (!currentRideId) return;
    const prevCurrentRideId = prevCurrentRideIdRef.current;
    const isReassignment = prevCurrentRideId && prevCurrentRideId !== currentRideId;
    if (!isReassignment) {
      setScrollRequest((n) => n + 1);
    } else {
      pendingReassignmentRef.current = prevCurrentRideId;
      refreshStatuses();
    }
    prevCurrentRideIdRef.current = currentRideId;
  }, [currentRideId, refreshStatuses]);

  // Resolves a reassignment queued above once a status snapshot taken AFTER
  // the refreshStatuses() call lands. `rows` (and therefore
  // statusByRideIdRef) updates first on that same render — its effect is
  // declared earlier, above — so this always reads the fresh value.
  useEffect(() => {
    const prevCurrentRideId = pendingReassignmentRef.current;
    if (!prevCurrentRideId) return;
    pendingReassignmentRef.current = null;
    const outgoingStillInProgress =
      statusByRideIdRef.current.get(prevCurrentRideId) === RIDE_STATUSES.IN_PROGRESS;
    if (outgoingStillInProgress) setScrollRequest((n) => n + 1);
  }, [statuses]);

  const loading = (classesLoading || ridesLoading) && rows.length === 0;

  // #2703 -- per-region loading flags. Each independently-fetched resource
  // exposes its OWN "first answer still in flight" flag (useRemoteResource's
  // `loading` is true only until the first settle for the current
  // [show, ring] key, and again after a key change; a background refetch
  // never raises it), so a region can show its own loading state and paint
  // the moment ITS data lands, never waiting on the slowest resource on the
  // screen. A FAILED fetch settles `loading` to false (the error paths own
  // that wording, #2144/#2233), so these never spin forever on an error.
  // Ring-scoped fetches (rides, breaks, ring-state, summary) answer an
  // instant empty/undefined while no ring is selected yet, which would read
  // as a settled "nothing here" for the window before AnnouncerContext's
  // one-shot default-ring resolution lands. While the show HAS rings to
  // resolve to (or its class list is still loading) but no ring is selected
  // yet (a persisted ring is already a valid fetch key, and a later snap
  // re-keys the fetches back to loading), those regions are still loading. A show with no rings at all
  // never resolves, so it is deliberately not pending: that is a real empty.
  // Before the show itself has arrived (the shared show list still loading)
  // every region is pending too: there is nothing to have answered yet.
  const showPending = showLoading && !showId;
  const ringPending =
    showPending || (!selectedRing && !ringResolved && (classesLoading || rings.length > 0));
  const regionLoading = useMemo(
    () => ({
      classes: classesLoading || showPending,
      rides: ridesLoading || ringPending,
      breaks: breaksLoading || ringPending,
      ringState: ringStateLoading || ringPending,
      summary: summaryLoading || ringPending,
    }),
    [classesLoading, showPending, ridesLoading, breaksLoading, ringStateLoading, summaryLoading, ringPending],
  );

  const value = useMemo(
    () => ({
      selectedShow,
      selectedRing,
      online,
      browserOnline,
      showId,
      showAllDays,
      setShowAllDays,
      autoScrollEnabled,
      setAutoScrollEnabled,
      todayKey,
      rings,
      // #2140 — the day spine needs an INDEPENDENT day switcher (arbitrary
      // days, not the legacy #892 today-vs-all toggle above), so it works
      // off the raw, already-fetched classes/rides/breaks rather than
      // filteredRides/filteredBreaks (which are scoped to showAllDays).
      // Purely additive: no new hooks, no new polls.
      classes,
      rides,
      breaks,
      filteredRides,
      filteredBreaks,
      statuses,
      statusesUnknown,
      sheetsByRideId,
      ringState,
      ringTruth,
      ringStateKnown,
      liveClassId,
      summaryByClassId,
      isClassPlacedFromSummary,
      presenceRows,
      rows,
      // #3391 -- every ride row of the ring, NOT day-scoped (see allRows).
      allRows,
      rideRowsByClassId,
      resultsByClassId,
      handleClassResults,
      currentRideId,
      scrollRequest,
      lastUpdated,
      isStale,
      hasFetchError,
      hasRidesFetchError,
      handleRefresh,
      dismissAlert,
      refreshing,
      loading,
      regionLoading,
      liveNow,
      // #3557 -- the show-wide change tick for the all-rings list.
      wholeShowTick,
    }),
    [
      selectedShow,
      selectedRing,
      online,
      browserOnline,
      showId,
      showAllDays,
      setShowAllDays,
      autoScrollEnabled,
      setAutoScrollEnabled,
      todayKey,
      rings,
      classes,
      rides,
      breaks,
      filteredRides,
      filteredBreaks,
      statuses,
      statusesUnknown,
      sheetsByRideId,
      ringState,
      ringTruth,
      ringStateKnown,
      liveClassId,
      summaryByClassId,
      isClassPlacedFromSummary,
      presenceRows,
      rows,
      // #3391 -- every ride row of the ring, NOT day-scoped (see allRows).
      allRows,
      rideRowsByClassId,
      resultsByClassId,
      handleClassResults,
      currentRideId,
      scrollRequest,
      lastUpdated,
      isStale,
      hasFetchError,
      hasRidesFetchError,
      handleRefresh,
      dismissAlert,
      refreshing,
      loading,
      regionLoading,
      liveNow,
      wholeShowTick,
    ],
  );

  return <RingDataContext.Provider value={value}>{children}</RingDataContext.Provider>;
}

export function useRingData() {
  const ctx = useContext(RingDataContext);
  if (!ctx) {
    throw new Error("useRingData must be used within a RingDataProvider");
  }
  return ctx;
}
